<script lang="ts" setup>
import type { GradeCode } from '~/models/Grade'
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import type { ContentProductContainer } from '~/models/Content/ContentProductContainer'
import type { ContentProduct } from '~/models/Content/ContentProduct'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useRoute, useRouter }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { firstOf } from '~/utils/queryUtils'
import { setTitle } from '~/utils/dom'
import { useAuthStore } from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { ColorTheme } from '~/models/Content/Color'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useTeacherGuide from '~/composables/useTeacherGuide'
import { useMedia } from '~/composables/useMedia'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import { useAppColor } from '~/composables/useAppColor'
import useAccordion from '~/composables/useAccordion'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import Select from '~/components/ui/Select.vue'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import ProductGenericSection from '~/components/products/ProductGenericSection.vue'
import Image from '~/components/media/Image.vue'
import Home8To10BannerArticle from '~/components/home/home8to10/Home8To10BannerArticle.vue'
import ProductGenericCard from '~/components/cards/ProductGenericCard.vue'

const props = defineProps<{ product: ContentProduct }>()

const { t } = useI18n()
const { isTeacher, isAuthenticated } = storeToRefs(useAuthStore())
const { findContents } = useContentApi()
const { theme, style } = useColorTheme(computed(() => props.product.colorTheme ?? ColorTheme.Coral))
const { isHeader, isTeacherArticle } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()
const { getAccordion } = useAccordion()

useAppColor(theme.value[60])

const route = useRoute()
const router = useRouter()

const activeGrade = ref<GradeCode | null>((route.query.grade as GradeCode) || null)

const showTeacherButton = computed(() => isTeacher.value
  && !!props.product.teacherContentRelation?.destinationContentIds?.length)

const { teacherArticle } = useTeacherGuide({
  contentId: props.product.teacherContentRelation?.destinationContentIds[0] ?? 0,
  enabled: showTeacherButton,
  withContent: false,
})

const { data: correctionArticle } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!props.product.correctionsContentRelation?.destinationContentId),
  queryKey: computed(() => ['product-correction', props.product.locationId]),
  queryFn: () => findContents({
    contentIdCriterion: [Number(props.product.correctionsContentRelation!.destinationContentId)],
    mainLocationCriterion: true,
  }, 1).then(([data]) => data),
  staleTime: Infinity,
  retry: 1,
})

const { data: colophonArticle } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!props.product.colophonContentRelation?.destinationContentId),
  queryKey: computed(() => ['product-colophon', props.product.locationId]),
  queryFn: () => findContents({
    contentIdCriterion: [Number(props.product.colophonContentRelation!.destinationContentId)],
    mainLocationCriterion: true,
  }, 1).then(([data]) => data),
  staleTime: Infinity,
})

const contentRelationIds = computed(() => [
  ...props.product.learningPathRelation?.destinationContentIds ?? [],
])

const { data: containers, isLoading: isLoadingContainers } = useQuery({
  enabled: computed(() => isAuthenticated.value && contentRelationIds.value.length > 0),
  queryKey: computed(() => ['product-containers', props.product.locationId, ...contentRelationIds.value]),
  queryFn: () => findContents<ContentProductContainer>({
    contentIdCriterion: contentRelationIds.value,
    contentTypeCriterion: [ContentType.ProductContainer],
    mainLocationCriterion: true,
  }, contentRelationIds.value.length),
  staleTime: Infinity,
})

const containerLocationIds = computed(() => containers.value?.map(container => container.locationId) ?? [])

const { data: children, isLoading: isLoadingChildren } = useQuery({
  enabled: computed(() => isAuthenticated.value && containerLocationIds.value.length > 0),
  queryKey: computed(() => ['product-children', props.product.locationId, ...containerLocationIds.value, activeGrade.value]),
  queryFn: () => findContents<BaseItem>({
    parentLocationIdCriterion: containerLocationIds.value,
    sortField: 'priority',
    sortOrder: 'asc',
    gradeFieldCriterion: activeGrade.value ? [activeGrade.value] : [],
  }),
  staleTime: Infinity,
})

const { data: sections, isLoading: isLoadingSections } = useQuery({
  queryKey: ['product_relevant_list', props.product.pathString],
  queryFn: () => findContents<ContentSectionStandalone>({
    subtreeCriterion: [props.product.pathString],
    sortField: 'priority',
    sortOrder: 'asc',
    contentTypeCriterion: [ContentType.SectionStandalone],
  }, 2),
  enabled: computed(() => isAuthenticated.value && !!props.product.pathString),
  staleTime: Infinity,
  retry: 1,
})

const intro = computed(() => containers?.value?.at(0)?.intro)
const headers = computed(() => (children?.value ?? []).filter(item => isHeader(item)))
const other = computed(() => (children?.value ?? []).filter(item => !isHeader(item) && !isTeacherArticle(item)))
const isLoading = computed(() => isLoadingContainers.value || isLoadingChildren.value || bgMediaIsLoading.value)

const mediaContentId = computed(() => props.product.image && Number(props.product.image.destinationContentId))
const { isLoading: bgMediaIsLoading } = firstOf(useMedia(computed(() => mediaContentId.value ? [mediaContentId.value] : [])))

const sectionColors = computed(() => {
  if (!headers.value.length) {
    return `background-color: ${theme.value[50]}; color: ${theme.value[20]};`
  }
  return headers.value.map((_, index) => {
    const offset = sections.value?.length ? 1 : 0
    const backgroundColor = (index + offset) % 2 === 0 ? theme.value[60] : theme.value[50]
    return `background-color: ${backgroundColor}; color: ${theme.value.pop};`
  })
})

const otherColors = computed(() => {
  const backgroundColor = headers.value.length % 2 === 0 ? theme.value[60] : theme.value[50]
  return `background-color: ${backgroundColor}; color: ${theme.value.pop};`
})

const showGradeFilter = computed(() => props.product.grades.length > 1 && props.product.showGradeFilter)

onMounted(() => {
  setTitle(props.product.title)
})

watch(headers, () => {
  if (!headers.value.length || !props.product)
    return
  updateAccordion(props.product.locationId)
}, { immediate: true, deep: true })

function updateAccordion(headerLocationId: number) {
  const [locationId] = getAccordion(headerLocationId)
  const locationIndex = locationId
    ? headers.value.findIndex(content => locationId === content.locationId)
    : -1

  const accordionNr = Number(route.hash || (locationIndex + 1))
  if (!accordionNr)
    return

  router
    .replace({ ...route, hash: `#${accordionNr}` })
    .then(() => nextTick())
    .then(() => {
      setTimeout(() => {
        document
          .getElementById(`${accordionNr}`)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
      }, 200)
    })
}

watch(activeGrade, () => activeGrade.value && router.replace({
  query: { grade: activeGrade.value }
}), { immediate: true })
</script>

<template>
  <div
    :style
    class="mx-auto flex-1 pt-28 transition-all"
  >
    <AuSkeletonWrapper
      v-if="isLoading"
      class="max-w-screen-au mx-auto items-center space-y-20 px-4 sm:px-8"
    >
      <div class="flex w-full items-center gap-12">
        <AuSkeleton
          :background="theme[50]"
          height="300px"
          width="400px"
        />
        <div class="flex w-full flex-col gap-8">
          <AuSkeleton
            :background="theme[50]"
            height="40px"
            width="500px"
          />
          <div class="w-full space-y-2">
            <AuSkeleton
              :background="theme[50]"
              height="20px"
              width="550px"
            />
            <AuSkeleton
              :background="theme[50]"
              height="20px"
              width="500px"
            />
            <AuSkeleton
              :background="theme[50]"
              height="20px"
              width="450px"
            />
            <AuSkeleton
              :background="theme[50]"
              height="20px"
              width="520px"
            />
            <AuSkeleton
              :background="theme[50]"
              height="20px"
              width="400px"
            />
          </div>
        </div>
      </div>
      <div>
        <AuSkeleton
          :background="theme[50]"
          height="36px"
          width="120px"
        />
        <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
          <AuSkeleton
            v-for="i in 4"
            :key="i"
            :background="theme[50]"
            height="280px"
          />
        </ul>
      </div>
    </AuSkeletonWrapper>

    <section v-else class="max-w-screen-au relative mx-auto px-4 sm:px-8">
      <div :class="{ 'border-b border-dashed border-(--theme-pop) pb-14': sections?.length }">
        <div class="flex gap-8">
          <div>
            <Image
              v-if="mediaContentId"
              :class="!bgMediaIsLoading && 'animate-fade'"
              :content="mediaContentId"
              :skeleton="false"
              class="aspect-auto object-cover"
              width="250px"
            />
          </div>
          <div class="flex flex-col">
            <h1
              class="mb-4 text-3xl leading-none font-extrabold text-(--theme-5)"
              v-text="props.product.title"
            />
            <Teleport v-if="showGradeFilter" to="#header-navigation">
              <Select
                v-model="activeGrade"
                :options="props.product.grades"
                :option-label="grade => t(`metadata.grades.${grade}`)"
                :color="theme[50]"
                :placeholder="t('search.filters.allGrades')"
                size="xl"
                show-null-option
              />
            </Teleport>
            <div
              v-if="intro"
              class="prose text-(--theme-5)"
            >
              <RichTextRenderer :text="intro" />
            </div>
            <div v-if="teacherArticle || colophonArticle || correctionArticle" class="mt-6 flex items-center gap-3">
              <Button
                v-if="teacherArticle"
                :to="{ path: buildResourceUrl(teacherArticle) }"
                color="teacher"
                size="lg"
              >
                {{ t('filters.forTeacher') }}
              </Button>
              <Button
                v-if="colophonArticle"
                :color="theme[50]"
                :to="{ path: buildResourceUrl(colophonArticle) }"
                size="lg"
              >
                {{ t('credit') }}
              </Button>
              <Button
                v-if="correctionArticle"
                :color="theme[50]"
                :to="{ path: buildResourceUrl(correctionArticle) }"
                size="lg"
              >
                {{ t('bookCorrections') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isLoadingSections && sections?.length" class="mt-8 mb-12 space-y-4">
        <p class="text-xl font-semibold text-(--theme-pop) uppercase" v-text="t('login.news')" />
        <div
          class="grid gap-x-4"
          :class="sections.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 auto-rows-fr'"
        >
          <Home8To10BannerArticle
            v-for="section in sections"
            :key="section.locationId"
            :section="section"
          />
        </div>
      </div>
    </section>

    <ProductGenericSection
      v-for="(section, index) in headers"
      :key="`header_${section.locationId}`"
      :hash="route.hash"
      :index
      :is-collapsible="props.product.collapsible"
      :product
      :section
      :style="sectionColors[index]"
      :theme
    />

    <section
      v-if="other.length"
      :style="otherColors"
      class="relative"
    >
      <div class="max-w-screen-au mx-auto flex flex-col space-y-8 px-4 py-12 sm:px-8">
        <div class="flex flex-row">
          <h2
            v-if="headers.length"
            class="font-tiempos mr-4 text-4xl"
            v-text="t('labels.other')"
          />
        </div>
        <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
          <li
            v-for="item in other"
            :key="item.locationId"
          >
            <ProductGenericCard
              :resource="item"
              loading="lazy"
            />
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>
